import type { AdminUser } from '@/api';
import { typeMeta } from '@/pages/dashboard/projectTypes';
import { initials } from '@/pages/dashboard/format';

export default function UserDrawer({
  user,
  onClose,
  onImpersonate,
  onOverrideCredits,
  onToggleBan,
}: {
  user: AdminUser | null;
  onClose: () => void;
  onImpersonate: (u: AdminUser) => void;
  onOverrideCredits: (u: AdminUser) => void;
  onToggleBan: (u: AdminUser) => void;
}) {
  if (!user) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-surface border-l border-border overflow-y-auto">
        <div className="sticky top-0 bg-surface border-b border-border px-5 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-textp">User details</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-texts hover:text-textp hover:bg-surface2 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 shrink-0 rounded-full bg-accentSoft border border-accentLine flex items-center justify-center text-base font-medium text-accent">
              {initials(user.name || user.email)}
            </span>
            <div className="min-w-0">
              <p className="text-textp font-semibold truncate">{user.name}</p>
              <p className="text-sm text-texts truncate">{user.email}</p>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <span
              className={`whitespace-nowrap text-xs px-2 py-0.5 rounded-full ${
                user.role === 'admin'
                  ? 'bg-accentSoft text-accent'
                  : 'bg-surface2 text-texts border border-border'
              }`}
            >
              {user.role}
            </span>
            <span
              className={`whitespace-nowrap inline-flex items-center gap-1.5 text-xs ${
                user.banned ? 'text-error' : 'text-success'
              }`}
            >
              <i className={`${user.banned ? 'ri-forbid-line' : 'ri-checkbox-circle-line'}`} />
              {user.banned ? 'Banned' : 'Active'}
            </span>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="bg-bg border border-border rounded-lg p-3">
              <p className="text-xs text-texts">Credits</p>
              <p className="mt-1 text-lg font-mono text-textp">{user.credits}</p>
            </div>
            <div className="bg-bg border border-border rounded-lg p-3">
              <p className="text-xs text-texts">Tokens</p>
              <p className="mt-1 text-lg font-mono text-textp">{user.tokens.toLocaleString()}</p>
            </div>
            <div className="bg-bg border border-border rounded-lg p-3">
              <p className="text-xs text-texts">Projects</p>
              <p className="mt-1 text-lg font-mono text-textp">{user.projects.length}</p>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={() => onImpersonate(user)}
              disabled={user.banned || user.role === 'admin'}
              className="whitespace-nowrap flex-1 flex items-center justify-center gap-1.5 text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 rounded-lg transition-colors"
            >
              <i className="ri-login-box-line" />
              Log in as
            </button>
            <button
              onClick={() => onOverrideCredits(user)}
              className="whitespace-nowrap flex-1 flex items-center justify-center gap-1.5 text-sm font-medium text-textp bg-surface2 hover:bg-border border border-border px-3 py-2 rounded-lg transition-colors"
            >
              <i className="ri-flashlight-line" />
              Credits
            </button>
          </div>

          <button
            onClick={() => onToggleBan(user)}
            className={`whitespace-nowrap mt-2 w-full flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${
              user.banned
                ? 'text-success bg-surface2 hover:bg-border border border-border'
                : 'text-error bg-surface2 hover:bg-border border border-border'
            }`}
          >
            <i className={user.banned ? 'ri-check-line' : 'ri-forbid-line'} />
            {user.banned ? 'Unban user' : 'Ban user'}
          </button>

          <div className="mt-6 pt-5 border-t border-border">
            <h3 className="text-sm font-semibold text-textp">Projects</h3>
            {user.projects.length === 0 ? (
              <p className="mt-3 text-sm text-texts">No projects yet.</p>
            ) : (
              <div className="mt-3 space-y-2">
                {user.projects.map((p) => {
                  const type = typeMeta(p.type);
                  return (
                    <div
                      key={p.name}
                      className="flex items-center justify-between gap-3 bg-bg border border-border rounded-lg px-3 py-2.5"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-8 h-8 shrink-0 rounded-lg bg-accentSoft border border-accentLine flex items-center justify-center">
                          <i className={`${type.icon} text-accent`} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm text-textp truncate">{p.name}</p>
                          <p className="text-xs text-texts">{type.label}</p>
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-mono text-success">
                          {p.score ? `${p.score}/12` : '—'}
                        </p>
                        <p className="text-xs font-mono text-texts">{p.tokens.toLocaleString()} tok</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {(user.models || []).length > 0 && (
            <div className="mt-6 pt-5 border-t border-border">
              <h3 className="text-sm font-semibold text-textp">Tokens by model</h3>
              <div className="mt-3 space-y-2">
                {(user.models || []).map((m) => (
                  <div key={`${m.provider}/${m.model}`} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-textp truncate">{m.model || 'unknown'}</p>
                      <p className="text-xs text-texts truncate">{m.provider}</p>
                    </div>
                    <span className="shrink-0 text-sm font-mono text-textp">{m.tokens.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
